import { useEffect, useState } from 'react'
import { Combobox } from '@headlessui/react'
import { College, collegeAPI } from '../services/collegeAPI'

interface Props {
  id: string
  value: College | null
  onChange: (college: College | null) => void
  collegeType?: string
  tier?: string
  disabled?: boolean
}

export default function CollegeSelect({ id, value, onChange, collegeType, tier, disabled }: Props) {
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<College[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  useEffect(() => {
    let cancelled = false
    if (disabled) { setOptions([]); return }
    setOptions([])
    setLoading(true)
    setError(false)
    const timer = setTimeout(() => {
      collegeAPI.options({ q: query, college_type: collegeType || undefined, tier: tier || undefined })
        .then(({ data }) => { if (!cancelled) setOptions(data) })
        .catch(() => { if (!cancelled) setError(true) })
        .finally(() => { if (!cancelled) setLoading(false) })
    }, 200)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [query, collegeType, tier, disabled])
  return (
    <Combobox value={value} by="college_id" onChange={onChange} nullable disabled={disabled}>
      <div className="relative">
        <Combobox.Label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-2">College Name *</Combobox.Label>
        <div className="relative">
          <Combobox.Input id={id} autoComplete="off"
            displayValue={(college: College | null) => college?.college_name || ''}
            onChange={event => { setQuery(event.target.value); onChange(null) }}
            placeholder={disabled ? 'Select college type and tier first' : 'Search and select a college'}
            className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 disabled:bg-gray-100" />
          <Combobox.Button aria-label="Show colleges" className="absolute inset-y-0 right-0 px-3">⌄</Combobox.Button>
        </div>
        <Combobox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white shadow-lg">
          {options.map(college => (
            <Combobox.Option key={college.college_id} value={college}
              className={({ active }) => `cursor-pointer px-3 py-2 ${active ? 'bg-blue-100' : ''}`}>
              {college.college_name}
            </Combobox.Option>
          ))}
          {options.length === 0 && <li className="px-3 py-2 text-sm text-gray-500" role="presentation">
            {loading ? 'Loading colleges...' : error ? 'Unable to load colleges. Please try again.' : 'No colleges found. A recruiter must register the college first.'}
          </li>}
        </Combobox.Options>
      </div>
    </Combobox>
  )
}
